import type { ShareScope } from '@mathflat/domain/@entities/(Content)/constants'
import type { CurriculumApi } from '@mathflat/domain/@entities/Curriculum/api'
import { omit } from 'lodash-es'
import { EMPTY_NUMBER_ID } from 'src/constant/common'
/* eslint-disable no-useless-escape */
/* eslint-disable no-useless-backreference */
import { CheckableColl } from 'src/models/CheckableColl'
import CheckableMap from 'src/models/CheckableMap'

import { mergeQueryString } from '@/api/_lib/mergeQueryString'
import type { SchoolGradeData } from '@/api/studentHistory.api.ts'
import worksheetApi from '@/api/worksheet.api'
import type { 학습지필터 } from '@/contentComponents/makeLessonMaterial/(filter)/_deprecated/service/MakeLessonMaterialFilter.service'
import type { DownloadWorksheetT } from '@/contentComponents/modal/Download/WorksheetDownload.service'
import type { LessonClass } from '@/dto'
import {
  ArithmeticCurriculumKeyWorksheet,
  ArithmeticWorksheetColl,
  LegacyArithmeticWorksheet,
  MakeProblem,
  MakeProblemColl,
  MakeWorksheetFilterOption,
  Worksheet,
  WorksheetColl,
  WorksheetDesign,
  WorksheetDesignOption,
} from '@/dto'
import { LegacyArithmeticWorksheetColl } from '@/dto/Worksheet/LegacyArithmeticWorksheetColl'
import { RouteName } from '@/router/RouteName'
import { splitFilenameAndExtension } from '@/utils/download'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

class WorksheetController implements Controller {
  adapter = {
    worksheet: makeAdapter({
      from(
        response:
          | res.MyWorksheet
          | res.EntranceTestWorksheet
          | res.RecommendWorksheet
          | res.EssayWorksheet
          | res.TextbookWorksheet
          | res.ChapterTestWorksheet
          | res.PracticeTestWorksheet
          | res.ShareWorksheet,
      ) {
        return new Worksheet(response)
      },
    }),
    // 다른 기관 학습지 검색 API(OpenSearch)는 메인 API 와 필드명이 일부 다르다.
    sharedCustomWorksheet: makeAdapter({
      from({ titlePrefix, createdAt, autoScored, ...rest }: res.SharedCustomWorksheet) {
        return new Worksheet({
          ...rest,
          titleTag: titlePrefix,
          createDatetime: createdAt,
          autoScorable: autoScored,
        })
      },
    }),
    worksheetColl: makeAdapter({
      from(
        response:
          | res.MyWorksheet[]
          | res.EntranceTestWorksheet[]
          | res.RecommendWorksheet[]
          | res.EssayWorksheet[]
          | res.TextbookWorksheet[]
          | res.ChapterTestWorksheet[]
          | res.ArithmeticWorksheet[]
          | res.PracticeTestWorksheet[]
          | res.ShareWorksheet[],
      ) {
        const worksheetList = response.map((res) => new Worksheet(res))
        return new CheckableColl(CheckableMap.fromArr(worksheetList, 'id' as keyof Worksheet))
      },
    }),
    legacyArithmetic: makeAdapter({
      from(response: res.LegacyArithmeticWorksheet) {
        return new LegacyArithmeticWorksheet(response)
      },
    }),
    worksheetDesign: makeAdapter({
      from(response: res.WorksheetDesign) {
        return new WorksheetDesign(response)
      },

      fromDetail(response: res.WorksheetDesignDetail) {
        return new WorksheetDesignOption(response)
      },
    }),
    problem: makeAdapter({
      from(problem: res.MakeWorksheetProblem, nullableOrder: boolean) {
        return new MakeProblem({
          ...problem,
          _concept: problem.concept,
          tagTop: problem.tagTop,
          favorite: problem.favorite ?? false,
          defaultOrder: nullableOrder ? problem.defaultOrder : null,
          sequence: problem.concept.sequence,
          conceptId: problem.concept.id,
          conceptName: problem.concept.name,
          conceptKey: problem.concept.key,
          boxIndex: problem.problemSource?.id ?? null,
        })
      },
    }),
    myDbProblemDetail: makeAdapter({
      from(detail: res.MyDbProblemDetail) {
        return new MakeProblem({
          id: detail.detailId,
          type: detail.latestVersion?.type ?? detail.problemType ?? 'SINGLE_CHOICE',
          level: detail.level,
          conceptId: detail.conceptId,
          conceptName: detail.conceptName,
          problemImageUrl: detail.latestVersion?.problemImageUrl ?? detail.box?.url ?? '',
          answerImageUrl: detail.latestVersion?.answerImageUrl ?? '',
          solutionImageUrl: detail.latestVersion?.solutionImageUrl ?? '',
          autoScoredType: detail.latestVersion?.autoScoredType ?? 'IMPOSSIBLE',
          tagTop: detail.tagTop ?? null,
          boxIndex: detail.box?.index ?? null,
        })
      },
    }),
    designTemplateId: {
      from(response: res.WorksheetDetail<'WEAK_WORKSHEET' | 'WORKSHEET'>) {
        if (response.pdf) {
          response.pdf.designTemplateId =
            response.pdf.designTemplateId === null ? EMPTY_NUMBER_ID : response.pdf.designTemplateId
          return response
        }
        return response
      },
      to(request: req.WorksheetDesignDetailUrlOptions) {
        const { designTemplateId, ...req } = request
        return req
      },
    },
    worksheetsByCurriculumKey: makeAdapter({
      from(curriculum: res.WorksheetsByCurriculumKey) {
        return curriculum.map((chapter) => {
          const arithmeticWorksheets = chapter.worksheet.map((worksheet) =>
            'assignList' in worksheet
              ? new Worksheet(worksheet)
              : new ArithmeticCurriculumKeyWorksheet(worksheet),
          )
          return {
            curriculumKey: chapter.curriculumKey,
            curriculumType: chapter.curriculumType,
            curriculumName: chapter.curriculumName,
            arithmeticWorksheetColl: new ArithmeticWorksheetColl(
              CheckableMap.fromArr(arithmeticWorksheets, 'id'),
            ),
          }
        })
      },
    }),
    chapters: makeAdapter({
      from(curriculum: CurriculumApi.Response.CurriculumInfo[]) {
        return curriculum.map((chapter) => {
          return {
            curriculumKey: chapter.key,
            curriculumName: chapter.name,
          }
        })
      },
    }),
  }

  private getApi =
    <T extends keyof typeof worksheetApi>(apiName: T) =>
    async (...params: Parameters<ValueOf<Pick<typeof worksheetApi, T>>>) => {
      const {
        data: {
          data: { content, last },
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } = await worksheetApi[apiName](...params)
      const coll = this.adapter.worksheetColl.from(content)
      return { coll, last } as { coll: CheckableColl<Worksheet>; last: boolean }
    }

  // data 래핑 제거된 버전
  private getApiV2 =
    <T extends keyof typeof worksheetApi>(apiName: T) =>
    async (...params: Parameters<ValueOf<Pick<typeof worksheetApi, T>>>) => {
      const {
        data: { content, last },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } = await worksheetApi[apiName](...params)
      const coll = this.adapter.worksheetColl.from(content)
      return { coll, last } as { coll: CheckableColl<Worksheet>; last: boolean }
    }

  private withScope =
    (searchShareScope: ShareScope) =>
    async (params: string) => {
      // mylist 진입 시 share-scope 필터 미주입 (myListId만으로 본인 학습지 조회)
      if (/[?&]myListId=/.test(params)) {
        return this.getApi('getMyWorksheet')(params)
      }
      return this.getApi('getMyWorksheet')(mergeQueryString(params, { searchShareScope }))
    }

  lessonMaterialFetch = {
    [RouteName.academyShareScopeWorksheet]: this.withScope('ACADEMY'),
    [RouteName.academyGroupShareScopeWorksheet]: this.withScope('ACADEMY_GROUP'),
    [RouteName.worksheetTrash]: this.getApi('getDeletedWorksheet'),
    [RouteName.recommend]: this.getApi('getRecommend'),
    [RouteName.essayWorksheet]: this.getApiV2('getEssayWorksheet'),
    [RouteName.textbookWorksheet]: this.getApi('getTextbookWorksheet'),
    [RouteName.practiceTest]: this.getApi('getPracticeTest'),
    [RouteName.practiceTestPair]: this.getApi('getPracticeTest'),
    [RouteName.themeEntranceTest]: this.getApi('getEntranceTest'),
    [RouteName.weeklyTest]: this.getApi('getWeeklyTest'),
    [RouteName.chapterTest]: this.getApi('getChapterTest'),
    [RouteName.generalTest]: this.getApi('getChapterTest'),
    [RouteName.kmmExamWorksheet]: this.getApi('getKMMExam'),
    [RouteName.satWorksheet]: this.getApiV2('getSatWorksheet'),
    [RouteName.policeMilitaryPracticeTest]: this.getApiV2('getPoliceMilitaryPracticeTest'),
    [RouteName.policeMilitaryPracticeTestPair]: this.getApiV2('getPoliceMilitaryPracticeTestPair'),
  }

  fetchDesignTemplate = async () => {
    const {
      data: { data },
    } = await worksheetApi.getDesignTemplate()

    return data
  }

  fetchLegacyArithmetic = async (params: req.LegacyArithmetic) => {
    const {
      data: {
        data: { content, last },
      },
    } = await worksheetApi.getLegacyArithmetic(params)
    const arithmeticArr = content.map(this.adapter.legacyArithmetic.from)

    return {
      arithmeticColl: new LegacyArithmeticWorksheetColl(CheckableMap.fromArr(arithmeticArr, 'id')),
      last,
    }
  }

  fetchShare = async (params: req.GetWorksheetShared) => {
    // 검색 API(OpenSearch)는 Spring Slice 를 envelope 없이 그대로 반환한다.
    const {
      data: { content, last },
    } = await worksheetApi.getShared(params)
    const problems = content.map(this.adapter.sharedCustomWorksheet.from)
    const workSheetMap = CheckableMap.fromArr(problems, 'id')
    return { workSheetColl: new WorksheetColl(workSheetMap), last }
  }

  // 학습지 상세 정보
  // TODO: 제네릭 받아서 filter값 추론하도록
  fetchWorksheetDetail = async <T extends 'WEAK_WORKSHEET' | 'WORKSHEET' = 'WORKSHEET'>(
    worksheetId: number,
    ignoredForDeleted?: boolean,
  ) => {
    const {
      data: { data },
    } = await worksheetApi.getWorksheetDetail<T>(worksheetId, ignoredForDeleted)
    return this.adapter.designTemplateId.from(data)
  }

  getAssignedStudentInfoByWorksheetId = async (id: number) => {
    const {
      data: { data },
    } = await worksheetApi.getAssignedStudentInfoByWorksheetId({ id })
    return data
  }

  // 디자인옵션 리스트
  fetchWorksheetDesign = async (pageQueryString: string) => {
    const {
      data: {
        data: { content, last },
      },
    } = await worksheetApi.getWorksheetDesign(pageQueryString)

    const worksheetDesignList = content.map(this.adapter.worksheetDesign.from)
    return {
      list: worksheetDesignList,
      last,
    }
  }

  fetchWorksheetDesignDetail = async (id: WorksheetDesign['id']) => {
    const {
      data: { data },
    } = await worksheetApi.getWorksheetDesignDetail(id)

    return this.adapter.worksheetDesign.fromDetail(data)
  }

  postWorksheetDesignDetail = async (data: req.WorksheetDesignDetailUrlOptions) => {
    await worksheetApi.postWorksheetDesignDetail(this.adapter.designTemplateId.to(data))
  }

  putWorksheetDesignDetail = async (id: number, data: req.WorksheetDesignDetailUrlOptions) => {
    await worksheetApi.putWorksheetDesignDetail(id, this.adapter.designTemplateId.to(data))
  }

  deleteWorksheetDesignDetail = async (id: number) => {
    await worksheetApi.deleteWorksheetDesignDetail(id)
  }

  // 1단계 - 학습지 만들기 : filterId를 통해 학습지 문제 수 조회
  fetchProblemCountByFilterId = async (filterId: string) => {
    const {
      data: { data: problemCount },
    } = await worksheetApi.getProblemCountByFilterId(
      { filterId },
      {
        useAbortController: true,
      },
    )
    return problemCount
  }

  // 2단계 - 내 문제 > 나의DB 탭 문제 목록. 응답이 CommonResponse 래퍼 없는 배열이라 data 를 그대로 매핑한다
  fetchMyDbFilterProblem = async (conceptIdList: req.MyDbFilterProblem['conceptIdList']) => {
    const { data } = await worksheetApi.postMyDbFilterProblem({ conceptIdList })
    return data.map((problem) => this.adapter.problem.from(problem, false))
  }

  // 학습지 병합 - filterId 만들기
  createFilterIdByMergedWorksheet = async (params: req.CreateFilterByMerge) => {
    const { data } = await worksheetApi.postFilterIdByMergedWorksheet(params)
    return data.filterId
  }

  // 1단계 - 단원유형별
  // 기존 req.CreateFilterByChapter 쓰이는 곳까지 파악하기 힘들어서 일단 제네릭 처리 ㅜ
  async createFilterIdByChapter<T extends 'Derivation' | 'Worksheet'>(
    params: T extends 'Derivation' ? req.CreateFilterByChapter : 학습지필터.단원유형별필터,
  ) {
    const data = {
      conceptIdList: [],
      excludedTopicIds: [],
      excludedSubTopicIds: [],
      problemList: null,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      filterId: params.filterId ?? undefined,
    }
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterIdByChapter(data, {
      useAbortController: true,
    })
    return filterId
  }

  async postFilterBySupportedWorkbook(params: 학습지필터.지원교재필터) {
    const adaptedParams: req.CreateFilterByPublicWorkbook = {
      problemList: null,
      problemOption: 'ALL' as const,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      pairX: params.pairX === 0 ? null : params.pairX,
      similarX: params.similarX === 0 ? null : params.similarX,
    }

    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterByPublicWorkbook(adaptedParams, {
      useAbortController: true,
    })
    return filterId
  }

  // 수능 모의고사
  createFilterByPracticeTest = async (params: 학습지필터.수능모의고사필터) => {
    const data = {
      curriculumIdList: null,
      problemList: null,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      filterId: params.filterId ?? undefined,
    }
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterByPracticeTest(data)
    return filterId
  }

  // 힉교별 내신 기출
  createFilterBySchoolTest = async (params: req.CreateFilterBySchoolTest) => {
    const filter = {
      ...params,
      pairX: params.pairX === 0 ? null : params.pairX,
      similarX: params.similarX === 0 ? null : params.similarX,
    }

    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterBySchoolTest(filter)
    return filterId
  }
  // 기출 업로드 - 기타 학습지
  createFilterByCustomPaper = async (data: req.CreateFilterByCustomPaper) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterByCustomPaper(data)
    return filterId
  }
  // 기출 업로드 : 기출 시험지
  createFilterBySchoolTestPaper = async (data: req.CreateFilterBySchoolTestPaper) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterBySchoolTestPaper(data)
    return filterId
  }

  // 기출 업로드 v2 : 원본 학습지 필터 생성
  createOriginalFilterBySchoolTestPaperV2 = async (
    data: req.CreateFilterBySchoolTestPaperOriginalV2,
  ) => {
    const {
      data: { filterId },
    } = await worksheetApi.createOriginalFilterBySchoolTestPaperV2(data)
    return filterId
  }

  // 기출 업로드 v2 : 유사 학습지 필터 생성
  createSimilarFilterBySchoolTestPaperV2 = async (
    payload: req.CreateFilterBySchoolTestPaperSimilarV2,
  ) => {
    const {
      data: { filterId },
    } = await worksheetApi.createSimilarFilterBySchoolTestPaperV2(payload)
    return filterId
  }

  createFilterByOneClick = async (data: req.CreateFilterByOneClick) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterIdByOneClick(data)
    return filterId
  }

  // 2단계 ------

  // UUID를 통한 학습지만들기 필터정보 가지고 오기
  fetchSearchFilterByFilterId = async (
    filterId: string,
    options?: { useAbortController?: boolean },
  ) => {
    const meta = { useAbortController: options?.useAbortController ?? true }
    const {
      data: { data },
    } = await worksheetApi.getSearchFilterByFilterId(filterId, meta)

    const option = new MakeWorksheetFilterOption(data)
    const { myDbProblemDetails } = data

    // API 응답에 myDbProblemDetailIds가 없을 경우에 대한 fallback
    if (myDbProblemDetails?.length && !option.myDbProblemDetailIds?.length) {
      option.update({ myDbProblemDetailIds: myDbProblemDetails.map((d) => d.detailId) })
    }

    /**
     * MY_DB_ORIGINAL 필터 특징
     * - /worksheet/problem을 호출하지 않음
     * - 원본 필터의 makeProblemColl 구성
     *   - problemList 정보가 있는 경우: 저장된 문제(problemList)기반으로 구성
     *   - problemList 정보가 없는 경우: myDbProblemDetails(실제 원본 문제)로 구성한다.
     */
    if (option.type === 'MY_DB_ORIGINAL') {
      // data.problemList가 있으면 올바른 problemId를 가진 문제로 사용 (수정/복제/재출제 경로)
      if (data.problemList && data.problemList.length > 0) {
        // problemIdToDetailIdMap을 활용하여 빈 problemSource를 보강 (불변 패턴)
        const { problemIdToDetailIdMap } = data
        const detailMap =
          problemIdToDetailIdMap && myDbProblemDetails?.length
            ? new Map(myDbProblemDetails.map((d) => [d.detailId, d]))
            : null

        const problemList = data.problemList
          .map((problem) => {
            if (!problem.problemSource && detailMap && problemIdToDetailIdMap) {
              const detailId = problemIdToDetailIdMap[String(problem.id)]
              const detail = detailId != null ? detailMap.get(detailId) : undefined
              if (detail) {
                return {
                  ...problem,
                  problemSource: {
                    type: 'CUSTOM_PAPER' as const,
                    id: detailId,
                    problemImageUrl: detail.box?.url ?? problem.problemImageUrl,
                  },
                }
              }
            }
            return problem
          })
          .map((problem) => this.adapter.problem.from(problem, true))
        return {
          option,
          makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
          myDbProblemDetails,
          rawProblemList: data.problemList as res.PaperFilterProblem[] | null,
        }
      }

      // myDbProblemDetails를 myDbProblemDetailIds 순서로 정렬 (기출 업로드 새로 만들기 경로)
      if (myDbProblemDetails?.length && option.myDbProblemDetailIds?.length) {
        const orderMap = new Map(option.myDbProblemDetailIds.map((id, index) => [id, index]))
        myDbProblemDetails.sort((a, b) => {
          const orderA = orderMap.get(a.detailId) ?? Number.MAX_SAFE_INTEGER
          const orderB = orderMap.get(b.detailId) ?? Number.MAX_SAFE_INTEGER
          return orderA - orderB
        })
      }

      const problemList =
        myDbProblemDetails && myDbProblemDetails.length > 0
          ? myDbProblemDetails.map((detail) => this.adapter.myDbProblemDetail.from(detail))
          : ([] as MakeProblem[])

      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
        myDbProblemDetails,
        rawProblemList: (data.problemList ?? null) as res.PaperFilterProblem[] | null,
      }
    }

    // 문제가 있으면 (수정)
    if (data.problemList && data.problemList.length) {
      const problemList = data.problemList.map((problem) =>
        this.adapter.problem.from(problem, true),
      )
      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
        myDbProblemDetails,
        rawProblemList: null as res.PaperFilterProblem[] | null,
      }
    } else {
      // 문제가 없으면 (1단계 진입) - 유사문제 리스트를 호출해서 채워서 리턴
      const {
        data: { data },
      } = await worksheetApi.postSearchWorksheetSimilarProblem(
        {
          filterId,
        },
        meta,
      )

      const problemList = data.map((problem) => this.adapter.problem.from(problem, true))

      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
        myDbProblemDetails,
        rawProblemList: null as res.PaperFilterProblem[] | null,
      }
    }
  }

  // (구) 유사문제, 새문제추가 -> (뉴) 새문제추가만
  fetchSimilarWorksheet = async (params: req.SearchSimilarProblem) => {
    const {
      data: { data },
    } = await worksheetApi.postSearchWorksheetSimilarProblem(params)
    const problemList = data.map((problem) => this.adapter.problem.from(problem, false))
    return new MakeProblemColl(CheckableMap.fromArr(problemList, 'id'))
  }

  // 3단계
  // 학습지 생성 / 복제후 생성
  postCreateMakeWorksheet = async (
    data: req.CreateWorksheet,
    options?: { useAbortController?: boolean },
  ) => {
    const studentWorksheetId = await worksheetApi.createWorksheet(data, {
      useAbortController: options?.useAbortController ?? true,
    })
    return studentWorksheetId
  }

  // 학습지형 입학고사 생성. 서버가 tag('입학고사')·shareScope(ACADEMY)를 고정하므로
  // 일반 생성 페이로드에서 해당 필드를 제거하고, 배정은 assignedGuestIds로 생성과 한 요청에 처리한다
  postCreateEntranceTestWorksheet = async (
    data: req.CreateWorksheet,
    options?: { useAbortController?: boolean },
  ) => {
    const params = {
      ...omit(data, ['tag', 'prefix', 'assignStudentIdList', 'shareScope']),
      assignedGuestIds: (data.assignStudentIdList ?? []).map(Number),
    }
    return await worksheetApi.createEntranceTestWorksheet(params, {
      useAbortController: options?.useAbortController ?? true,
    })
  }

  // 서버가 tag·shareScope 등을 고정하므로 생성과 동일하게 제거해 보낸다
  putEntranceTestWorksheet = async (
    worksheetId: res.CreverseEntranceWorksheetResponse['id'],
    data: req.CreateWorksheet,
  ) => {
    const params = omit(data, ['tag', 'prefix', 'assignStudentIdList', 'shareScope'])
    return await worksheetApi.putEntranceTestWorksheet(worksheetId, params)
  }

  // 배정된 시험지 3단계 수정 — PUT과 동일하게 서버가 고정하는 tag·prefix는 제거해 보낸다
  patchEntranceTestWorksheet = async (
    worksheetId: res.CreverseEntranceWorksheetResponse['id'],
    data: req.EditAssignedWorksheet,
  ) => {
    const params = omit(data, ['tag', 'prefix'])
    return await worksheetApi.patchEntranceTestWorksheet(worksheetId, params)
  }

  // 그룹 오답 - 일괄 오답관리 - 학생별 오답학습지
  postGroupWrongWorksheetPerStudent = async (data: req.CreateEachStudentWorksheet) => {
    return await worksheetApi.postGroupWrongWorksheetPerStudent(data)
  }

  // 학습지 수정 — PUT 스키마(CustomWorksheetUpdateRequest)에 shareScope가 없어 서버가 무시하므로 제거해 보낸다
  putMakeWorksheet = async (worksheetId: Worksheet['id'], data: req.CreateWorksheet) => {
    return await worksheetApi.putWorksheetFix(worksheetId, omit(data, ['shareScope']))
  }
  // 출제 된 학습지 수정
  patchMakeWorksheet = async (worksheetId: number, data: req.EditAssignedWorksheet) => {
    return await worksheetApi.patchWorksheetFix(worksheetId, data)
  }
  fetchWorksheetDownload = async (
    params: req.WorksheetDownloadParams,
    type?: DownloadWorksheetT['type'],
  ) => {
    const { data } = await worksheetApi.getWorksheetDownload(params, type)
    const { presignedUrl, fileName } = data

    const { fileExt } = splitFilenameAndExtension(fileName)
    return { url: presignedUrl, fullFileName: fileName, fileExt }
  }

  deleteWorksheets = async (worksheetIds: number[]) =>
    await worksheetApi.deleteWorksheets(worksheetIds)

  forceDeleteWorksheets = async (worksheetIds: Worksheet['id'][]) =>
    await worksheetApi.forceDeleteWorksheets(worksheetIds)

  deleteWorksheetsInTrash = async (worksheetIds: Worksheet['id'][]) =>
    await worksheetApi.deleteWorksheetsInTrash(worksheetIds)

  emptyWorksheetInTrash = async () => await worksheetApi.emptyWorksheetInTrash()

  patchWorksheetShared = async (data: req.PatchWorksheetShared) =>
    await worksheetApi.patchWorksheetShared(data)

  copyWorksheet = async (worksheetId: number, requestBody: req.CopyWorksheet) => {
    await worksheetApi.postWorksheetCopy(worksheetId, requestBody)
  }

  fetchArithmeticWorksheetsByCurriculumKey = async (params: req.CurriculumGroups) => {
    const {
      data: {
        data: { content },
      },
    } = await worksheetApi.getArithmeticWorksheetsByCurriculumKey({
      ...params,
      page: params.page || 0,
      size: params.size || 999999,
    })
    return this.adapter.worksheetsByCurriculumKey.from(content as res.WorksheetsByCurriculumKey)
  }

  fetchChapters = async (params: req.CurriculumChapters) => {
    const {
      data: { data },
    } = await worksheetApi.getChapters({
      ...params,
    })
    return this.adapter.chapters.from(data)
  }

  patchAccessModifier = async (requestBody: req.AccessModifier) => {
    await worksheetApi.patchAccessModifier(requestBody)
  }

  getGradeXLS = async (
    { schoolType, grade }: SchoolGradeData,
    dateParams: {
      startYear: number
      endYear: number
      startWeek: number
      endWeek: number
      periodDescription: string
    },
  ) => {
    return worksheetApi.getGradeXLS({ schoolType, grade }, dateParams)
  }

  getClassXLS = async (
    classId: LessonClass['id'],
    dateParams: {
      startYear: number
      endYear: number
      startWeek: number
      endWeek: number
      periodDescription: string
    },
  ) => {
    return worksheetApi.getClassXLS(classId, dateParams)
  }

  // 학교별 기출 유사 학습지 검색
  fetchSimilarWorksheetsBySchoolTest = async (
    params: Parameters<typeof worksheetApi.getSimilarWorksheetsBySchoolTest>[0],
  ) => {
    const { data } = await worksheetApi.getSimilarWorksheetsBySchoolTest(params)
    return data
  }

  // 학교별 기출 유사 학습지 수량 검색
  fetchWorksheetCountBySchoolTest = async (
    params: Parameters<typeof worksheetApi.getSimilarWorksheetCountBySchoolTest>[0],
  ) => {
    const { data } = await worksheetApi.getSimilarWorksheetCountBySchoolTest(params)
    return data
  }

  // 학습지 복구
  restoreWorksheet = async (worksheetIds: number[]) => {
    await worksheetApi.restoreWorksheet(worksheetIds)
  }
}

export const worksheetController = new WorksheetController()
